import { getStadiumImageUrl } from '../constants/config';
import { ADMIN_SEARCH_TYPES, NEWS_ENTITY_TYPES, fromServerSearchType, normalizeSearchItem, toServerSearchType } from './adminSearch';
import { extractEntity, extractList } from './tournamentsApi';

export const NEWS_PAGE_SIZE = 50;
export const NEWS_TAG_KEY_RE = /^[A-Za-z0-9_\-]{1,255}$/;

export const getNewsImageUrl = (image, rev) => {
  const url = getStadiumImageUrl(image);
  if (!url) return '';
  if (rev == null || rev === '') return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(String(rev))}`;
};

/** One retry with a fresh cache-buster — helps after a 404 was cached (common on iOS). */
export const newsImageOnError = (event) => {
  const img = event?.currentTarget;
  if (!img || img.dataset.retried === '1') return;
  img.dataset.retried = '1';
  const raw = String(img.currentSrc || img.src || '');
  if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) return;
  try {
    const url = new URL(raw, window.location.origin);
    url.searchParams.set('v', String(Date.now()));
    img.src = url.toString();
  } catch (error) {
    const base = raw.split('?')[0];
    img.src = `${base}?v=${Date.now()}`;
  }
};

export const newsCardPath = (id) => `/news/${id}`;
export const newsNewPath = () => '/news/new';

const bindingName = (item, type, field) => {
  if (!item || typeof item !== 'object') return '';
  const nested = item[type];
  const id = Number(item[field] ?? nested?.id);
  if (!Number.isFinite(id) || id <= 0) return '';
  const name = nested?.name
    || nested?.title
    || item[`${type}_name`]
    || item[`${field.replace(/_id$/, '')}_name`]
    || (type === 'championship' ? item.champ_name : '')
    || (type === 'match' ? (item.game_name || item.match_name) : '');
  return String(name || `#${id}`);
};

export const parseNewsTag = (item) => {
  if (item == null) return null;
  const key = normalizeNewsTagKey(typeof item === 'string' ? item : item.key);
  if (!key) return null;
  const source = typeof item === 'object' ? item : {};
  const bindings = { ...(source.bindings || {}), migrationId: String(source.bindings?.migrationId || source.migrationId || source.migration_id || '') };
  ADMIN_SEARCH_TYPES.forEach(({ type, field }) => {
    if (bindings[type]?.id) {
      bindings[type] = normalizeSearchItem(bindings[type], type) || bindings[type];
      return;
    }
    const nested = source[type];
    const id = Number(source[field] ?? nested?.id);
    if (!Number.isFinite(id) || id <= 0) {
      bindings[type] = null;
      return;
    }
    bindings[type] = normalizeSearchItem({
      ...(nested && typeof nested === 'object' ? nested : {}),
      id,
      name: bindingName(source, type, field) || `#${id}`,
    }, type);
  });
  return {
    key,
    bindings,
    news_count: Number.isFinite(Number(source.news_count)) ? Number(source.news_count) : source.news_count,
  };
};

export const parseNewsTags = (tags) => {
  const seen = new Set();
  const list = [];
  (Array.isArray(tags) ? tags : []).forEach((item) => {
    const tag = parseNewsTag(item);
    if (!tag || seen.has(tag.key)) return;
    seen.add(tag.key);
    list.push(tag);
  });
  return list;
};

export const newsTagKeys = (tags) => parseNewsTags(tags).map((tag) => tag.key);

export const serializeNewsTags = (tags) => parseNewsTags(tags).map((tag) => {
  const row = { key: tag.key };
  ADMIN_SEARCH_TYPES.forEach(({ type, field }) => {
    const id = Number(tag.bindings?.[type]?.id);
    if (Number.isFinite(id) && id > 0) row[field] = id;
  });
  if (tag.bindings?.migrationId) row.migrationId = tag.bindings.migrationId;
  return row;
});

export const serializeNewsTagKeys = (tags) => parseNewsTags(tags).map((tag) => ({ key: tag.key }));

export const newsEntityKey = (item) => {
  const parsed = normalizeSearchItem(item, item?.type);
  return parsed ? `${parsed.type}:${parsed.id}` : '';
};

export const parseNewsEntities = (list, { fromServer = false } = {}) => {
  const seen = new Set();
  const items = [];
  (Array.isArray(list) ? list : []).forEach((item) => {
    const type = fromServer ? fromServerSearchType(item?.type) : item?.type;
    const parsed = normalizeSearchItem({ ...item, type }, type);
    if (!parsed || !NEWS_ENTITY_TYPES.some(({ type: allowed }) => allowed === parsed.type)) return;
    const key = `${parsed.type}:${parsed.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(parsed);
  });
  return items;
};

export const serializeNewsEntities = (list) => {
  const seen = new Set();
  const rows = [];
  (Array.isArray(list) ? list : []).forEach((item) => {
    const parsed = normalizeSearchItem(item, item?.type);
    if (!parsed || !NEWS_ENTITY_TYPES.some(({ type }) => type === parsed.type)) return;
    const key = `${parsed.type}:${parsed.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ type: toServerSearchType(parsed.type), id: parsed.id });
  });
  return rows;
};

export const extractNewsEntities = (item) => parseNewsEntities(item?.entities, { fromServer: true });

export const bindingsFromFilters = (filters = {}) => {
  const bindings = {};
  NEWS_ENTITY_TYPES.forEach(({ type }) => {
    bindings[type] = filters[type] || null;
  });
  return bindings;
};

export const normalizeNewsTagKey = (value) => (
  String(value || '').trim().replace(/\s+/g, '_')
);

export const isValidNewsTagKey = (value) => NEWS_TAG_KEY_RE.test(normalizeNewsTagKey(value));

export const extractNewsList = (data) => {
  if (Array.isArray(data?.items)) {
    return {
      items: data.items,
      total: Number.isFinite(Number(data.total)) ? Number(data.total) : data.items.length,
    };
  }
  if (Array.isArray(data)) {
    return { items: data, total: data.length };
  }
  const entity = extractEntity(data);
  if (Array.isArray(entity.items)) {
    return {
      items: entity.items,
      total: Number.isFinite(Number(entity.total)) ? Number(entity.total) : entity.items.length,
    };
  }
  return { items: [], total: 0 };
};

export const extractNewsItem = (data) => {
  const entity = extractEntity(data);
  if (!entity || entity.id == null) return null;
  return entity;
};

export const extractNewsTags = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.tags)) return data.tags;
  if (Array.isArray(data?.data)) return data.data;
  return extractList(data);
};

export const formatNewsDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const escapeAttr = (value) => escapeHtml(value).replace(/'/g, '&#39;');

const safeUrl = (value) => {
  const url = String(value || '').trim();
  if (!url || /^\s*javascript:/i.test(url) || /^\s*data:text\/html/i.test(url)) return '';
  return url;
};

/** Resolve relative /images/... paths against BASE_URL (includes /api when set). */
const safeMediaUrl = (value) => {
  const url = safeUrl(value);
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  return getStadiumImageUrl(url) || url;
};

const isHtmlContent = (value) => (
  /<(p|div|br|strong|b|em|i|a|img|h[1-6]|blockquote|ul|ol|li|span)\b/i.test(String(value || ''))
);

export const looksLikeNewsMarkdown = (value) => (
  /(^|\n)#{1,3}\s+\S|(^|\n)>\s+\S|(\*\*[^*]+\*\*|__[^_]+__|!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\))/m.test(String(value || ''))
);

const markdownInlineToHtml = (raw) => {
  const tokens = [];
  const hold = (html) => {
    const key = `\u0000${tokens.length}\u0000`;
    tokens.push(html);
    return key;
  };

  let text = String(raw || '');
  text = text.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_, alt, url) => {
    const safe = safeMediaUrl(url);
    return safe ? hold(`<img src="${escapeAttr(safe)}" alt="${escapeAttr(alt)}">`) : '';
  });
  text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, url) => {
    const safe = safeUrl(url);
    return safe ? hold(`<a href="${escapeAttr(safe)}">${escapeHtml(label)}</a>`) : escapeHtml(label);
  });
  text = escapeHtml(text);
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  text = text.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  tokens.forEach((html, index) => {
    text = text.split(`\u0000${index}\u0000`).join(html);
  });
  return text;
};

export const markdownToHtml = (markdown) => {
  const value = String(markdown || '').replace(/\r\n/g, '\n').trim();
  if (!value) return '';
  return value.split(/\n{2,}/).map((block) => {
    const lines = block.split('\n').map((line) => line.replace(/\s+$/g, ''));
    while (lines.length && !lines[0].trim()) lines.shift();
    while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
    if (!lines.length) return '';
    const heading = lines[0].match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const html = `<h${heading[1].length}>${markdownInlineToHtml(heading[2])}</h${heading[1].length}>`;
      const rest = lines.slice(1).filter((line) => line.trim());
      if (!rest.length) return html;
      return `${html}<p>${rest.map(markdownInlineToHtml).join('<br>')}</p>`;
    }
    if (lines[0].startsWith('>')) {
      const body = lines.map((line) => line.replace(/^>\s?/, '')).join('<br>');
      return `<blockquote>${markdownInlineToHtml(body)}</blockquote>`;
    }
    return `<p>${lines.map(markdownInlineToHtml).join('<br>')}</p>`;
  }).filter(Boolean).join('');
};

export const newsTextToHtml = (text) => {
  const value = String(text || '');
  if (!value.trim()) return '';
  if (isHtmlContent(value)) return sanitizeNewsHtml(value);
  return markdownToHtml(value);
};

const BLOCK_TAGS = new Set(['p', 'div', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'pre', 'hr']);

/** Inline/markdown fragment (no block boundaries). */
const serializeInline = (node) => {
  if (!node) return '';
  if (node.nodeType === 3) return String(node.nodeValue || '').replace(/\u00a0/g, ' ');
  if (node.nodeType !== 1) return '';
  const tag = node.tagName.toLowerCase();
  const children = () => [...node.childNodes].map(serializeInline).join('');
  if (tag === 'br') return '\n';
  if (tag === 'img') {
    const src = safeUrl(node.getAttribute('src'));
    const alt = node.getAttribute('alt') || '';
    return src ? `![${alt}](${src})` : '';
  }
  if (tag === 'a') {
    const href = safeUrl(node.getAttribute('href'));
    const label = children().replace(/\s+/g, ' ').trim() || href;
    return href ? `[${label}](${href})` : label;
  }
  if (tag === 'strong' || tag === 'b') {
    const text = children().replace(/\s+/g, ' ').trim();
    return text ? `**${text}**` : '';
  }
  if (tag === 'em' || tag === 'i') {
    const text = children().replace(/\s+/g, ' ').trim();
    return text ? `*${text}*` : '';
  }
  return children();
};

const hasBlockChild = (node) => (
  node && node.nodeType === 1
  && [...node.children].some((child) => BLOCK_TAGS.has(child.tagName.toLowerCase()))
);

/** Текст внутри цитаты: вложенные p/div → отдельные строки. */
const serializeQuoteBody = (node) => {
  const lines = [];
  const pushLine = (value) => {
    String(value || '')
      .split('\n')
      .map((line) => line.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .forEach((line) => lines.push(line));
  };
  [...node.childNodes].forEach((child) => {
    if (child.nodeType === 3) {
      pushLine(child.nodeValue);
      return;
    }
    if (child.nodeType !== 1) return;
    const tag = child.tagName.toLowerCase();
    if (tag === 'br') return;
    if (tag === 'p' || tag === 'div') {
      if (hasBlockChild(child)) {
        serializeQuoteBody(child).forEach((line) => lines.push(line));
      } else {
        pushLine(serializeInline(child));
      }
      return;
    }
    if (tag === 'blockquote') {
      serializeQuoteBody(child).forEach((line) => lines.push(line));
      return;
    }
    pushLine(serializeInline(child));
  });
  return lines;
};

const serializeBlockquote = (node) => {
  const lines = serializeQuoteBody(node);
  if (!lines.length) return '';
  return lines.map((line) => (line ? `> ${line}` : '>')).join('\n');
};

const serializeHeading = (node, tag) => {
  const level = tag === 'h1' ? 1 : (tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6' ? 3 : 2);
  const text = serializeInline(node).replace(/\s+/g, ' ').trim();
  return text ? `${'#'.repeat(level)} ${text}` : '';
};

export const htmlToMarkdown = (html) => {
  if (typeof document === 'undefined') return String(html || '');
  const root = document.createElement('div');
  root.innerHTML = sanitizeNewsHtml(html);
  const parts = [];
  const push = (value) => {
    const text = String(value || '').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
    if (text) parts.push(text);
  };

  const walk = (parent) => {
    let inlineBuf = '';
    const flushInline = () => {
      const text = inlineBuf.replace(/\s+/g, ' ').trim();
      inlineBuf = '';
      if (text) push(text);
    };

    [...parent.childNodes].forEach((node) => {
      if (node.nodeType === 3) {
        inlineBuf += String(node.nodeValue || '').replace(/\u00a0/g, ' ');
        return;
      }
      if (node.nodeType !== 1) return;
      const tag = node.tagName.toLowerCase();

      if (tag === 'blockquote') {
        flushInline();
        push(serializeBlockquote(node));
        return;
      }
      if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
        flushInline();
        push(serializeHeading(node, tag));
        return;
      }
      if (tag === 'ul' || tag === 'ol') {
        flushInline();
        [...node.children].forEach((item) => {
          const text = serializeInline(item).replace(/\s+/g, ' ').trim();
          if (text) push(text);
        });
        return;
      }
      if (tag === 'br') {
        inlineBuf += '\n';
        return;
      }
      if (tag === 'p' || tag === 'div') {
        flushInline();
        if (hasBlockChild(node)) {
          walk(node);
        } else {
          push(serializeInline(node));
        }
        return;
      }
      inlineBuf += serializeInline(node);
    });
    flushInline();
  };

  walk(root);
  return parts.join('\n\n').trim();
};

export const sanitizeNewsHtml = (html) => {
  if (typeof document === 'undefined') return String(html || '');
  const root = document.createElement('div');
  root.innerHTML = String(html || '');
  root.querySelectorAll('script,style,iframe,object,embed').forEach((node) => node.remove());
  root.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const val = String(attr.value || '');
      if (name.startsWith('on') || ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(val))) {
        node.removeAttribute(attr.name);
      }
    });
  });
  root.querySelectorAll('img[src]').forEach((node) => {
    const resolved = safeMediaUrl(node.getAttribute('src'));
    if (resolved) node.setAttribute('src', resolved);
    else node.removeAttribute('src');
  });
  return root.innerHTML;
};

export const isNewsEnabled = (value) => Number(value) !== 0;
